import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { WorkspaceProvider, ChatProvider, PreviewProvider, useWorkspaceContext } from './ChatPage/providers';
import Workspace from './ChatPage/workspace';
import RightWorkspace from './ChatPage/workspace/RightWorkspace';
import ResumePage from './ChatPage/workspace/ResumePage';
import CoverLetterPage from './ChatPage/workspace/CoverLetterPage';
import NotFound from './ChatPage/workspace/NotFound';
import { BoostDialog, BoostProgress, BoostReport } from './ChatPage/boost';
import StyleCustomizer from './ChatPage/customizer';
import PricingModal from '../components/PricingModal';
import JDVariantModal from '../components/JDVariantModal';
import { Sparkles } from 'lucide-react';
import AIDebugPanel from '../components/AIDebugPanel';

function ChatPageContent() {
  const { activeTab } = useWorkspaceContext();
  const getMiddleTabName = () => {
    if (activeTab === 'editor') return 'Editor';
    if (activeTab === 'ats') return 'ATS';
    if (['jd', 'jd_match'].includes(activeTab)) return 'JD Match';
    if (activeTab === 'templates') return 'Templates';
    return 'Chat';
  };

  const {
    authToken,
    user,
    credits,
    setCredits,
    setPlan,
    showPricing,
    setShowPricing,
    resumeData,
    isStreaming,
    errorBanner,
    setErrorBanner,
    toast,
    setToast,
    editMode,
    setEditMode,
    mobileTab,
    setMobileTab,
    showMobileActions,
    setShowMobileActions,
    lightningActive,
    theme,
    photoInputRef,
    resumeFileInputRef,
    isUploadingPhoto,
    isUploadingResume,
    showJdModal,
    setShowJdModal,
    handlePhotoUpload,
    handleResumeUpload,
    handleVariantCreated,
  } = useAppContext();

  return (
    <div className="flex flex-col h-screen font-['Inter',sans-serif] overflow-hidden" style={{ background: 'var(--surface-0)' }}>
      
      {/* ── Pricing Modal ── */}
      <PricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
        authToken={authToken}
        user={user}
        credits={credits ?? 0}
        theme={theme}
        onCreditsUpdate={(newCredits, newPlan) => {
          setCredits(newCredits);
          if (newPlan) setPlan(newPlan);
          setShowPricing(false);
        }}
      />

      {/* ── Boost Modals ── */}
      <BoostDialog />
      <BoostProgress />
      <BoostReport />

      {/* ── Customize Drawer ── */}
      <StyleCustomizer />

      {/* ── Error Banner ── */}
      {errorBanner && (
        <div className="shrink-0 flex items-center justify-between gap-3 px-5 py-2.5 bg-red-900/30 border-b border-red-500/30 text-sm z-50">
          <span className="text-red-300 flex items-center gap-2">
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
            {errorBanner.message}
          </span>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={errorBanner.retryFn}
              className="px-3 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-medium transition-all"
            >
              ⟳ Retry
            </button>
            <button
              onClick={() => setErrorBanner(null)}
              className="px-2 py-1 rounded-lg text-red-400/60 hover:text-red-300 text-xs transition-all"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── Hidden file inputs ── */}
      {/* NOTE: resumeFileInputRef input was moved to AppGlobalOverlays (App.js) so it is
          always mounted when ImportChoiceModal fires — independent of ChatPage mount state. */}
      <input type="file" ref={photoInputRef} onChange={handlePhotoUpload} accept="image/png,image/jpeg,image/webp" className="hidden" />

      {/* ── Mobile More-Actions slide-up sheet ── */}
      {showMobileActions && (
        <div className="md:hidden fixed inset-0 z-[200] flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowMobileActions(false)} />
          <div className="relative animate-slide-up rounded-t-2xl overflow-hidden" style={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.08)', borderBottom: 'none' }}>
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-700" />
            </div>
            <div className="px-5 py-4 space-y-2.5 pb-8">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-3">Actions</p>
              <button onClick={() => { resumeFileInputRef.current?.click(); setShowMobileActions(false); }} disabled={isUploadingResume}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all active:scale-[0.98] disabled:opacity-50"
                style={{ background: 'rgba(20,184,166,0.08)', border: '1px solid rgba(20,184,166,0.2)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(20,184,166,0.15)' }}>
                  {isUploadingResume
                    ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: '#2dd4bf' }}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.75 12-3-3m0 0-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" /></svg>}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-white">Upload Resume</p>
                  <p className="text-xs" style={{ color: '#475569' }}>Import PDF or DOCX · 5 credits</p>
                </div>
              </button>
              {resumeData && (
                <button onClick={() => { photoInputRef.current?.click(); setShowMobileActions(false); }} disabled={isUploadingPhoto}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all active:scale-[0.98] disabled:opacity-50"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: '#94a3b8' }}><path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z" /></svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-white">Add Profile Photo</p>
                    <p className="text-xs" style={{ color: '#475569' }}>JPEG, PNG or WebP</p>
                  </div>
                </button>
              )}
              {resumeData && (
                <button onClick={() => { setEditMode(m => !m); setShowMobileActions(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all active:scale-[0.98]"
                  style={editMode ? { background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' } : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: editMode ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)' }}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: editMode ? '#34d399' : '#94a3b8' }}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" /></svg>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-white">{editMode ? 'Done Editing' : 'Edit Resume'}</p>
                    <p className="text-xs" style={{ color: '#475569' }}>{editMode ? 'Exit inline edit mode' : 'Click fields to edit directly'}</p>
                  </div>
                  {editMode && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399' }}>ON</span>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── 3-COLUMN WORKSPACE ── */}
      <Workspace />

      {/* ── JD Variant Creation Modal ── */}
      <JDVariantModal
        isOpen={showJdModal}
        onClose={() => setShowJdModal(false)}
        onVariantCreated={handleVariantCreated}
        credits={credits ?? 0}
        authToken={authToken}
        theme={theme}
      />

      {/* ── Mobile Bottom Tab Navigation ── */}
      {resumeData && (
        <div className={`md:hidden fixed bottom-0 left-0 right-0 z-50 flex backdrop-blur-xl border-t shadow-2xl transition-all ${theme === 'dark'
          ? 'bg-[#0c0c0f]/95 border-zinc-800/70'
          : 'bg-white/95 border-gray-200'
          }`}>
          {/* Menu */}
          <button onClick={() => setMobileTab(0)}
            className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 text-[10px] font-semibold transition-all relative ${mobileTab === 0 ? 'text-blue-400' : 'text-slate-500'}`}>
            {mobileTab === 0 && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" />}
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={mobileTab === 0 ? 2.2 : 1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
            Menu
          </button>

          {/* Chat */}
          <button onClick={() => setMobileTab(1)}
            className={`flex-1 flex-col flex items-center justify-center py-3 gap-1 text-[10px] font-semibold transition-all relative ${mobileTab === 1 ? 'text-blue-400' : 'text-slate-500'}`}>
            {mobileTab === 1 && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" />}
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={mobileTab === 1 ? 2.2 : 1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
            </svg>
            {getMiddleTabName()}
            {isStreaming && <span className="absolute top-2 right-[calc(50%-16px)] w-2 h-2 rounded-full bg-emerald-400 animate-pulse ring-2 ring-emerald-400/20" />}
          </button>

          {/* Preview */}
          <button onClick={() => setMobileTab(2)}
            className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 text-[10px] font-semibold transition-all relative ${mobileTab === 2 ? 'text-blue-400' : 'text-slate-500'}`}>
            {mobileTab === 2 && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" />}
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={mobileTab === 2 ? 2.2 : 1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
            Preview
            {resumeData && mobileTab !== 2 && <span className="absolute top-2 right-[calc(50%-16px)] w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-emerald-400/20" />}
          </button>
        </div>
      )}

      {/* ── Lightning strike overlay ── */}
      {lightningActive && (
        <div className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden">
          <div className="absolute top-[45%] left-0 w-full h-[6px] bg-gradient-to-r from-transparent via-blue-400 via-cyan-500 via-emerald-300 to-transparent opacity-90 blur-[1px] animate-lightning-strike" />
          <div className="absolute top-[45%] left-0 w-full h-[2px] bg-white opacity-100 animate-lightning-strike" />
          <div className="absolute right-[20%] top-[40%] w-[180px] h-[180px] rounded-full border-2 border-blue-500/30 bg-blue-600/10 blur-[8px] animate-energy-crackle" />
        </div>
      )}

      {/* ── Toast Notification ── */}
      {toast && (
        <div 
          className="fixed bottom-6 right-6 z-[999] flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl backdrop-blur-md border animate-fade-up"
          style={{ 
            background: theme === 'dark' ? 'rgba(24, 24, 27, 0.85)' : 'rgba(255, 255, 255, 0.9)',
            borderColor: theme === 'dark' ? 'rgba(29, 155, 240, 0.3)' : 'rgba(29, 155, 240, 0.2)',
            color: theme === 'dark' ? '#f4f4f5' : '#18181b',
            animation: 'slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards'
          }}
        >
          <div className="w-6 h-6 rounded-full bg-sky-600/20 text-sky-400 flex items-center justify-center shrink-0">
            <Sparkles size={14} />
          </div>
          <span className="text-[12px] font-medium leading-tight">{toast.message}</span>
          <button 
            onClick={() => setToast(null)}
            className="text-slate-400 hover:text-slate-200 transition-colors ml-2"
          >
            ✕
          </button>
        </div>
      )}
      <AIDebugPanel />
    </div>
  );
}

export default function ChatPage() {
  return (
    <WorkspaceProvider>
      <ChatProvider>
        <PreviewProvider>
          <Routes>
            <Route element={<ChatPageContent />}>
              <Route index element={<Navigate to="/app/resume/master/chat" replace />} />
              <Route path="dashboard" element={<RightWorkspace />} />
              <Route path="documents" element={<RightWorkspace />} />
              <Route path="vault" element={<RightWorkspace />} />
              <Route path="tracker" element={<RightWorkspace />} />
              <Route path="profile" element={<RightWorkspace />} />
              <Route path="resume/:resumeId/chat" element={<ResumePage mode="chat" />} />
              <Route path="resume/:resumeId/editor" element={<ResumePage mode="editor" />} />
              <Route path="resume/:resumeId/preview" element={<Navigate to="../editor" replace />} />
              <Route path="cover-letter/:clId" element={<CoverLetterPage />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </PreviewProvider>
      </ChatProvider>
    </WorkspaceProvider>
  );
}
